import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { InstallGuideSheet } from './InstallGuideSheet';
import { isIosDevice } from './installHelp';

/** The browser's install prompt event (Chrome/Edge/Android). Not in lib.dom yet, so typed locally. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'cg.install.dismissed';
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
const wasDismissed = () => {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * A dismissible "Install ClassGuardian" banner across the top of the dashboard (#213). It shows until the
 * app is installed or the parent dismisses it (remembered per device). On Chrome/Edge/Android it triggers
 * the browser's install prompt; on iOS Safari (no prompt event) it shows the Share → Add to Home Screen
 * hint. It renders nothing when already installed, dismissed, or when the browser offers no way to install.
 */
export function InstallBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(() => isStandalone() || wasDismissed());
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setHidden(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (hidden) return null;
  const ios = isIosDevice();
  // Nothing to offer: not iOS and no install prompt (already installed, or a browser without PWA install).
  if (!ios && !deferred) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode etc. — just hide for this session */
    }
    setHidden(true);
  };
  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setHidden(true);
  };

  return (
    <>
      <div className="install-bar" role="note">
        <Download size={16} />
        <span className="grow">Install ClassGuardian on this device for quick access and notifications.</span>
        {ios ? (
          // iOS gives no install prompt; open per-browser Add-to-Home-Screen steps instead.
          <button className="btn small" onClick={() => setGuideOpen(true)}>
            Add to Home Screen
          </button>
        ) : (
          <button className="btn small" onClick={install}>
            Install
          </button>
        )}
        <button className="icon-btn" onClick={dismiss} aria-label="Dismiss install banner">
          <X size={16} />
        </button>
      </div>
      {guideOpen && <InstallGuideSheet onClose={() => setGuideOpen(false)} />}
    </>
  );
}
